import React, { useEffect, useMemo } from 'react';

import { Controller, UseFormReturn } from 'react-hook-form';

import { useQueryGetCardActivityTypes } from 'api/queries/getCardActivityTypes.query';

import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { IActivityType } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { Select } from 'components/DesignSystem/Select';

type Option = {
    id?: number;
    value: string;
    label: string;
};

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    value?: IActivityType[];
    isSelected?: boolean;
    versionId?: number;
}

export const CardActivityFilter = ({
    name,
    methods,
    filtersStatusRef,
    handleFilters,
    value,
    isSelected = true,
    versionId,
}: Props) => {
    const { data, isSuccess } = useQueryGetCardActivityTypes({
        metaDataId: versionId,
    });

    const options: Option[] = useMemo(() => {
        return (
            data?.map((option) => {
                return {
                    id: option?.id,
                    value: option.name,
                    label: option.label,
                };
            }) ?? []
        );
    }, [data]);

    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
            return;
        }

        signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
    }, [value, name, filtersStatusRef, isSuccess]);

    const handleValue = (formValue?: string[]): Option[] => {
        const result: Option[] = [];
        if (!formValue?.length) return result;
        options.forEach((listOptionElement: Option) => {
            const currentFormValueElement = formValue.find(
                (formValueElement) => formValueElement === listOptionElement.value,
            );

            if (currentFormValueElement) {
                result.push(listOptionElement);
            }
        });

        return result;
    };

    const handleChange = (selected: readonly ISelectOption<string>[]) => {
        methods?.setValue(
            name,
            selected.map((option) => option.value),
        );
        handleFilters && handleFilters();
    };

    return isSelected ? (
        <div className={classes.selectWrapper}>
            <div data-testid={'battlecards-filters-activity-type'} className={classes.activityCodesSelectWrapper}>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<string>, true>
                            {...field}
                            options={options}
                            isMulti
                            id={'battlecards-filters-activity-type'}
                            value={handleValue(value)}
                            onChange={handleChange}
                            hideSelectedOptions={true}
                            label={FilterLabels.ACTIVITY_TYPE}
                        />
                    )}
                    name={name}
                    control={methods?.control}
                />
            </div>
        </div>
    ) : null;
};
